import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 0;
  border-top: 1px solid #dae5ec;
  gap: 0.25rem;
`;

const PaginationButton = styled.button<{
    active?: boolean;
    disabled?: boolean;
    isEllipsis?: boolean;
}>`
  padding: 0.5rem 0.75rem;
  border: 1px solid #dae5ec;
  background-color: ${props => {
        if (props.isEllipsis) return 'transparent';
        return props.active ? props.theme?.primaryColor : '#ffffff';
    }};
  color: ${props => {
        if (props.isEllipsis) return '#627d95';
        return props.active ? '#ffffff' : '#061c2e';
    }};
  border-radius: ${({ theme }) => theme.radius};
  cursor: ${props => {
        if (props.disabled || props.isEllipsis) return 'not-allowed';
        return 'pointer';
    }};
  font-size: 0.875rem;
  min-width: 2.5rem;
  
  &:hover:not(:disabled):not([data-ellipsis="true"]) {
    background-color: ${props => props.active ? props.theme?.hoverColor : props.theme?.lozengeColor};
  }
  
  &:disabled {
    opacity: 0.5;
  }
  
  ${props => props.isEllipsis && `
    border: none;
    cursor: default;
    pointer-events: none;
  `}
`;

const PageInfo = styled.div`
  font-size: 0.875rem;
  color: #627d95;
  margin: 0 1rem;
`;

interface SmartPaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    showInfo?: boolean;
    totalItems?: number;
    itemsPerPage?: number;
}

const SmartPagination: React.FC<SmartPaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    showInfo = false,
    totalItems = 0,
    itemsPerPage = 10
}) => {
    const [t] = useTranslation();

    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const delta = 2; // Number of pages to show around current page
        const pages: (number | string)[] = [];

        // Always show first page
        pages.push(1);

        // Calculate start and end of middle range
        const start = Math.max(2, currentPage - delta);
        const end = Math.min(totalPages - 1, currentPage + delta);

        // Add ellipsis after first page if needed
        if (start > 2) {
            pages.push('...');
        }

        // Add middle pages
        for (let i = start; i <= end; i++) {
            if (i !== 1 && i !== totalPages) {
                pages.push(i);
            }
        }

        // Add ellipsis before last page if needed
        if (end < totalPages - 1) {
            pages.push('...');
        }

        // Always show last page if there's more than one page
        if (totalPages > 1) {
            pages.push(totalPages);
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <PaginationWrapper>
            {/* Previous button */}
            <PaginationButton
                disabled={currentPage <= 1}
                onClick={() => onPageChange(currentPage - 1)}
            >
                {t('PREVIOUS', 'Previous')}
            </PaginationButton>

            {/* Page numbers */}
            {pageNumbers.map((page, index) => (
                <PaginationButton
                    key={`${page}-${index}`}
                    active={page === currentPage}
                    isEllipsis={page === '...'}
                    onClick={() => typeof page === 'number' && onPageChange(page)}
                    data-ellipsis={page === '...'}
                >
                    {page}
                </PaginationButton>
            ))}

            {/* Next button */}
            <PaginationButton
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange(currentPage + 1)}
            >
                {t('NEXT', 'Next')}
            </PaginationButton>

            {/* Optional info */}
            {showInfo && totalItems > 0 && (
                <PageInfo>
                    {t('PAGINATION_INFO', `Showing ${startItem}-${endItem} of ${totalItems}`, {
                        start: startItem,
                        end: endItem,
                        total: totalItems
                    })}
                </PageInfo>
            )}
        </PaginationWrapper>
    );
};

export default SmartPagination; 